/**
 * useLandingARKflowLandingReactionLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait LandingARKflowLandingReaction
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, LandingARChemReactionState, LandingARKflowLandingReactionEventPayloadMap } from '@app/shared';

export interface LandingARKflowLandingReactionConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  atomRadius: number;
  backgroundColor: string;
  colorA: string;
  colorB: string;
  equation: string;
  height: number;
  kf: number;
  kr: number;
  matchMode: string;
  pairCount: number;
  substeps: number;
  width: number;
}

const DEFAULT_LANDING_AR_KFLOW_LANDING_REACTION_CONFIG = {
  atomRadius: 12,
  backgroundColor: "",
  colorA: "series-2",
  colorB: "series-1",
  equation: "A + B ⇌ AB",
  height: 400,
  kf: 0.35,
  kr: 0.25,
  matchMode: "",
  pairCount: 4,
  substeps: 8,
  width: 600,
} as LandingARKflowLandingReactionConfig;

type LandingARKflowLandingReactionSlot = 'main';
export interface LandingARKflowLandingReactionLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<LandingARKflowLandingReactionSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingARChemReactionState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingARKflowLandingReactionEventPayloadMap]: (payload?: LandingARKflowLandingReactionEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingARKflowLandingReactionConfig;
}

export function useLandingARKflowLandingReactionLogic(config?: Partial<LandingARKflowLandingReactionConfig>): LandingARKflowLandingReactionLogicReturn {
  const mergedConfig: LandingARKflowLandingReactionConfig = { ...DEFAULT_LANDING_AR_KFLOW_LANDING_REACTION_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'LandingARChemReactionState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<LandingARChemReactionState>;
  const data: Record<string, EntityRow[]> = { 'LandingARChemReactionState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<LandingARKflowLandingReactionSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingARChemReactionState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pairs'] = (Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => ({ id: ["p", ["", j].join('')].join(''), bonded: false })));
        fields = { ...(fields ?? {}), 'pairs': _fields['pairs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['atoms'] = ((Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => (() => { const rowY = (400 * ((+(j ?? 0) + 0.5) / 4)); const homeA = (600 * 0.25); const homeB = (600 * 0.75); return [{ id: ["a", ["", j].join('')].join(''), x: homeA, y: rowY, element: "A" as const, radius: 12, color: "series-2" as const, home: homeA, pairId: ["p", ["", j].join('')].join('') }, { id: ["b", ["", j].join('')].join(''), x: homeB, y: rowY, element: "B" as const, radius: 12, color: "series-1" as const, home: homeB, pairId: ["p", ["", j].join('')].join('') }]; })())) ?? []).flat();
        fields = { ...(fields ?? {}), 'atoms': _fields['atoms'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bonds'] = ((fields?.pairs ?? []) as readonly { bonded?: boolean; id?: string }[]).map((pr) => ({ from: ["a", ["", getNestedValue(pr, "id")].join('')].join(''), to: ["b", ["", getNestedValue(pr, "id")].join('')].join(''), type: "single" as const, state: "default" as const }));
        fields = { ...(fields ?? {}), 'bonds': _fields['bonds'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "A" as const, value: ["", 4].join('') }, { label: "B" as const, value: ["", 4].join('') }, { label: "AB" as const, value: "0" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["", [4, " A+B pairs at rest — press Step to run one reaction interval"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('REACTION_UPDATED', _p, { trait: 'LandingARKflowLandingReaction' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReaction', { event: 'REACTION_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReaction' } }); })({ atoms: fields?.atoms, bonds: fields?.bonds, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pairs'] = (Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => ({ id: ["p", ["", j].join('')].join(''), bonded: false })));
        fields = { ...(fields ?? {}), 'pairs': _fields['pairs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['atoms'] = ((Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => (() => { const rowY = (400 * ((+(j ?? 0) + 0.5) / 4)); const homeA = (600 * 0.25); const homeB = (600 * 0.75); return [{ id: ["a", ["", j].join('')].join(''), x: homeA, y: rowY, element: "A" as const, radius: 12, color: "series-2" as const, home: homeA, pairId: ["p", ["", j].join('')].join('') }, { id: ["b", ["", j].join('')].join(''), x: homeB, y: rowY, element: "B" as const, radius: 12, color: "series-1" as const, home: homeB, pairId: ["p", ["", j].join('')].join('') }]; })())) ?? []).flat();
        fields = { ...(fields ?? {}), 'atoms': _fields['atoms'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bonds'] = ((fields?.pairs ?? []) as readonly { bonded?: boolean; id?: string }[]).map((pr) => ({ from: ["a", ["", getNestedValue(pr, "id")].join('')].join(''), to: ["b", ["", getNestedValue(pr, "id")].join('')].join(''), type: "single" as const, state: "default" as const }));
        fields = { ...(fields ?? {}), 'bonds': _fields['bonds'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "A" as const, value: ["", 4].join('') }, { label: "B" as const, value: ["", 4].join('') }, { label: "AB" as const, value: "0" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["", [4, " A+B pairs at rest — press Step to run one reaction interval"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('REACTION_UPDATED', _p, { trait: 'LandingARKflowLandingReaction' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReaction', { event: 'REACTION_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReaction' } }); })({ atoms: fields?.atoms, bonds: fields?.bonds, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on LEARN_STEP)
        {

        const decided = ((fields?.pairs ?? []) as readonly { bonded?: boolean; id?: string }[]).map((pr) => (() => { const prevBonded = getNestedValue(pr, "bonded"); const roll = Math.random(); return (() => { const newBonded = (prevBonded ? (+(roll ?? 0) >= 0.25) : (+(roll ?? 0) < 0.35)); return { id: getNestedValue(pr, "id"), bonded: newBonded, prevBonded: prevBonded }; })(); })());

        {

        const formedCount = (((decided ?? []) as readonly EntityRow[]).filter((d) => (getNestedValue(d, "bonded") && !getNestedValue(d, "prevBonded")))).length;

        const brokenCount = (((decided ?? []) as readonly EntityRow[]).filter((d) => (!getNestedValue(d, "bonded") && getNestedValue(d, "prevBonded")))).length;

        {

        const moved = (Array.from({ length: Math.ceil(8) }, (_, i) => i)).reduce<EventPayloadValue>((acc, i) => ((acc ?? []) as readonly EntityRow[]).map((a) => (() => { const pr = ((decided as readonly EventPayloadValue[] | undefined) ?? []).find((d) => (getNestedValue(d, "id") === getNestedValue(a, "pairId"))); return (() => { const bonded = getNestedValue(pr, "bonded"); return (() => { const targetX = (bonded ? (600 * 0.5) : getNestedValue(a, "home")); return Object.assign({}, a, { x: (Number(getNestedValue(a, "x") ?? 0) + (Number(targetX ?? 0) - Number(getNestedValue(a, "x") ?? 0)) * ((1 / 8))) }); })(); })(); })()), fields?.atoms);

        {

        const bonds = ((decided ?? []) as readonly EntityRow[]).map((d) => ({ from: ["a", ["", getNestedValue(d, "id")].join('')].join(''), to: ["b", ["", getNestedValue(d, "id")].join('')].join(''), type: "single" as const, state: ((getNestedValue(d, "bonded") && !getNestedValue(d, "prevBonded")) ? "forming" : ((!getNestedValue(d, "bonded") && getNestedValue(d, "prevBonded")) ? "breaking" : (getNestedValue(d, "bonded") ? "highlight" : "default"))) }));

        const newPairs = ((decided ?? []) as readonly EntityRow[]).map((d) => ({ id: getNestedValue(d, "id"), bonded: getNestedValue(d, "bonded") }));

        const bondedCount = (((decided ?? []) as readonly EntityRow[]).filter((d) => getNestedValue(d, "bonded"))).length;

        const totalPairs = ((decided ?? []) as { readonly length: number }).length;

        const netChange = (+(formedCount ?? 0) - +(brokenCount ?? 0));

        _fields['atoms'] = moved;
        fields = { ...(fields ?? {}), 'atoms': _fields['atoms'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pairs'] = newPairs;
        fields = { ...(fields ?? {}), 'pairs': _fields['pairs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bonds'] = bonds;
        fields = { ...(fields ?? {}), 'bonds': _fields['bonds'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "A" as const, value: ["", (+(totalPairs ?? 0) - +(bondedCount ?? 0))].join('') }, { label: "B" as const, value: ["", (+(totalPairs ?? 0) - +(bondedCount ?? 0))].join('') }, { label: "AB" as const, value: ["", bondedCount].join('') }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ((netChange === 0) ? ["at equilibrium — ", [["", formedCount].join(''), [" formed, ", [["", brokenCount].join(''), [" broke this step, AB=", ["", bondedCount].join('')].join('')].join('')].join('')].join('')].join('') : ["reacting — ", [["", formedCount].join(''), [" formed, ", [["", brokenCount].join(''), [" broke this step, AB=", ["", bondedCount].join('')].join('')].join('')].join('')].join('')].join(''));
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        }

        }

        }

        _emits.push(((_p) => () => { eventBus.emit('REACTION_UPDATED', _p, { trait: 'LandingARKflowLandingReaction' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReaction', { event: 'REACTION_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReaction' } }); })({ atoms: fields?.atoms, bonds: fields?.bonds, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET' && ((fields?.active === true))) {
      // idle → idle (on LEARN_RESET)
        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pairs'] = (Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => ({ id: ["p", ["", j].join('')].join(''), bonded: false })));
        fields = { ...(fields ?? {}), 'pairs': _fields['pairs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['atoms'] = ((Array.from({ length: Math.ceil(4) }, (_, i) => i)).map(((j) => (() => { const rowY = (400 * ((+(j ?? 0) + 0.5) / 4)); const homeA = (600 * 0.25); const homeB = (600 * 0.75); return [{ id: ["a", ["", j].join('')].join(''), x: homeA, y: rowY, element: "A" as const, radius: 12, color: "series-2" as const, home: homeA, pairId: ["p", ["", j].join('')].join('') }, { id: ["b", ["", j].join('')].join(''), x: homeB, y: rowY, element: "B" as const, radius: 12, color: "series-1" as const, home: homeB, pairId: ["p", ["", j].join('')].join('') }]; })())) ?? []).flat();
        fields = { ...(fields ?? {}), 'atoms': _fields['atoms'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bonds'] = ((fields?.pairs ?? []) as readonly { bonded?: boolean; id?: string }[]).map((pr) => ({ from: ["a", ["", getNestedValue(pr, "id")].join('')].join(''), to: ["b", ["", getNestedValue(pr, "id")].join('')].join(''), type: "single" as const, state: "default" as const }));
        fields = { ...(fields ?? {}), 'bonds': _fields['bonds'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "A" as const, value: ["", 4].join('') }, { label: "B" as const, value: ["", 4].join('') }, { label: "AB" as const, value: "0" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = ["", [4, " A+B pairs at rest — press Step to run one reaction interval"].join('')].join('');
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        _emits.push(((_p) => () => { eventBus.emit('REACTION_UPDATED', _p, { trait: 'LandingARKflowLandingReaction' }); emitCascadeEvent(eventBus, 'LandingAR', 'LandingARKflowLandingReaction', { event: 'REACTION_UPDATED', payload: _p, source: { orbital: 'LandingAR', trait: 'LandingARKflowLandingReaction' } }); })({ atoms: fields?.atoms, bonds: fields?.bonds, readouts: fields?.readouts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_UPDATED' && ((fields?.active === true))) {
      // idle → idle (on LEARN_UPDATED)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingARKflowLandingReactionSlot[]; clear: LandingARKflowLandingReactionSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingARKflowLandingReactionSlot[], clear: [] as LandingARKflowLandingReactionSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as LandingARKflowLandingReactionSlot[], clear: [] as LandingARKflowLandingReactionSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as LandingARKflowLandingReactionSlot[], clear: [] as LandingARKflowLandingReactionSlot[] };
          case 'LEARN_UPDATED': return { write: ['main'] as LandingARKflowLandingReactionSlot[], clear: [] as LandingARKflowLandingReactionSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingARKflowLandingReactionSlot[], clear: ['main'] as LandingARKflowLandingReactionSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'LandingARChemReactionState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'LandingARKflowLandingReaction': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingARKflowLandingReactionEventPayloadMap]: (payload?: LandingARKflowLandingReactionEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingARKflowLandingReactionEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'LEARN_STEP': (payload?: LandingARKflowLandingReactionEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: LandingARKflowLandingReactionEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
    'REACTION_UPDATED': (payload?: LandingARKflowLandingReactionEventPayloadMap['REACTION_UPDATED']) => dispatch('REACTION_UPDATED', payload),
    'RESET': () => dispatch('RESET'),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
  };

  useUIEvents(enqueueEvent, 'LandingAR.LandingARKflowLandingReaction', ['INIT', 'MODE_SELECTED', 'LEARN_STEP', 'LEARN_RESET', 'LEARN_UPDATED'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_RESET' (from LandingARKflowLandingReactionClock) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_landingarkflowlandingreactionclock_to_learn_reset = eventBus.on('UI:LandingAR.LandingARKflowLandingReactionClock.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReaction', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    // Listen for 'LEARN_STEP' (from LandingARKflowLandingReactionClock) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_landingarkflowlandingreactionclock_to_learn_step = eventBus.on('UI:LandingAR.LandingARKflowLandingReactionClock.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReaction', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_UPDATED' (from LandingARKflowLandingReactionClock) → trigger 'LEARN_UPDATED'
    const unsub_learn_updated_from_landingarkflowlandingreactionclock_to_learn_updated = eventBus.on('UI:LandingAR.LandingARKflowLandingReactionClock.LEARN_UPDATED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingAR.LandingARKflowLandingReaction', 'LEARN_UPDATED')) return;
      enqueueEvent('LEARN_UPDATED', event.payload);
    });
    return () => {
      if (typeof unsub_learn_reset_from_landingarkflowlandingreactionclock_to_learn_reset === 'function') unsub_learn_reset_from_landingarkflowlandingreactionclock_to_learn_reset();
      if (typeof unsub_learn_step_from_landingarkflowlandingreactionclock_to_learn_step === 'function') unsub_learn_step_from_landingarkflowlandingreactionclock_to_learn_step();
      if (typeof unsub_learn_updated_from_landingarkflowlandingreactionclock_to_learn_updated === 'function') unsub_learn_updated_from_landingarkflowlandingreactionclock_to_learn_updated();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
